import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Users, ArrowLeft, ShoppingBag, MessageSquare, CalendarDays, CreditCard, Star, User, FileText, Sparkles } from "lucide-react";

// Swedish, human-readable line for each event type on the timeline.
const EVENT_META = {
  "order.created": { icon: ShoppingBag, color: "#D4B483", label: (p) => `La beställning #${p.order_number || "?"} · ${p.total ?? "?"} kr (${p.order_type === "delivery" ? "leverans" : "avhämtning"})` },
  "order.accepted": { icon: ShoppingBag, color: "#10B981", label: (p) => `Beställning #${p.order_number || "?"} accepterades (${p.estimated_time || "?"} min)` },
  "order.rejected": { icon: ShoppingBag, color: "#EF4444", label: (p) => `Beställning #${p.order_number || "?"} nekades${p.reason ? ` — ${p.reason}` : ""}` },
  "order.status_changed": { icon: ShoppingBag, color: "#93C5FD", label: (p) => `Beställning #${p.order_number || "?"}: ${p.from} → ${p.to}` },
  "order.cancelled": { icon: ShoppingBag, color: "#9A9AA2", label: (p) => `Beställning #${p.order_number || "?"} avbröts` },
  "review.submitted": { icon: Star, color: "#F59E0B", label: (p) => `Lämnade recension ${p.rating}/5 på #${p.order_number || "?"}` },
  "payment.recorded": { icon: CreditCard, color: "#10B981", label: (p) => `Betalade ${p.amount ?? "?"} kr (${p.method || "?"}) för #${p.order_number || "?"}` },
  "payment.removed": { icon: CreditCard, color: "#EF4444", label: (p) => `Betalning ångrades för #${p.order_number || "?"}` },
  "message.received": { icon: MessageSquare, color: "#93C5FD", label: (p) => `Skickade meddelande${p.channel ? ` via ${p.channel}` : ""}: "${p.preview || ""}"` },
  "booking.created": { icon: CalendarDays, color: "#D4B483", label: (p) => `Bokning ${p.date} ${p.time} (${p.party_size} gäster)` },
  "booking.status_changed": { icon: CalendarDays, color: "#93C5FD", label: (p) => `Bokning: ${p.from} → ${p.to}` },
  "booking.approval_required": { icon: CalendarDays, color: "#EF4444", label: (p) => `Bokning kräver godkännande (${p.party_size} gäster)` },
  "inquiry.created": { icon: MessageSquare, color: "#D4B483", label: (p) => `Förfrågan: ${p.subject || "utan ämne"}${p.party_size ? ` (${p.party_size} gäster)` : ""}` },
  "inquiry.status_changed": { icon: MessageSquare, color: "#93C5FD", label: (p) => `Förfrågan: ${p.from} → ${p.to}` },
  "inquiry.converted": { icon: FileText, color: "#A855F7", label: (p) => `Förfrågan blev offert ${p.quote_number || ""}` },
  "quote.created": { icon: FileText, color: "#D4B483", label: (p) => `Offert ${p.quote_number} skapades · ${p.total ?? 0} kr` },
  "quote.version_added": { icon: FileText, color: "#93C5FD", label: (p) => `Offert ${p.quote_number}: ny version v${p.version} · ${p.total} kr` },
  "quote.sent": { icon: FileText, color: "#93C5FD", label: (p) => `Offert ${p.quote_number} skickades (giltig ${p.valid_days} dagar)` },
  "quote.accepted": { icon: FileText, color: "#10B981", label: (p) => `Offert ${p.quote_number} accepterades · ${p.total} kr` },
  "quote.rejected": { icon: FileText, color: "#EF4444", label: (p) => `Offert ${p.quote_number} avböjdes${p.reason ? ` — ${p.reason}` : ""}` },
  "quote.expired": { icon: FileText, color: "#F59E0B", label: (p) => `Offert ${p.quote_number} gick ut` },
  "quote.converted": { icon: CalendarDays, color: "#10B981", label: (p) => `Offert ${p.quote_number} blev bokning ${p.date} ${p.time}` },
  "customer.created": { icon: User, color: "#10B981", label: () => "Skapade konto" },
  "customer.deleted": { icon: User, color: "#EF4444", label: () => "Raderade sitt konto" },
  "invoice.created": { icon: FileText, color: "#D4B483", label: (p) => `Faktura ${p.invoice_number} skapades · ${p.total} kr` },
  "invoice.paid": { icon: FileText, color: "#10B981", label: (p) => `Faktura ${p.invoice_number} betalades` },
  "invoice.sent": { icon: FileText, color: "#93C5FD", label: (p) => `Faktura ${p.invoice_number} skickades` },
};

export default function CustomersPanel() {
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);   // {customer, stats, events}
  const [loadingDetail, setLoadingDetail] = useState(false);

  const load = useCallback(async (s) => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/customers?search=${encodeURIComponent(s || "")}`);
      setCustomers(res.data.customers || []);
      setTotal(res.data.total || 0);
    } catch { toast.error("Kunde inte ladda kunder"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  const openCustomer = async (c) => {
    setLoadingDetail(true);
    try {
      const res = await api.get(`/admin/customers/${c.id}/timeline`);
      setSelected(res.data);
    } catch { toast.error("Kunde inte ladda kundens historik"); }
    finally { setLoadingDetail(false); }
  };

  const fmt = (s) => { try { return new Date(s).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" }); } catch { return ""; } };

  if (selected) {
    const { customer, stats, events } = selected;
    return (
      <div className="max-w-3xl" data-testid="customer-timeline">
        <button onClick={() => setSelected(null)} className="text-[#D4B483] hover:text-[#E5CFA5] text-sm inline-flex items-center gap-1.5 mb-4">
          <ArrowLeft className="w-4 h-4" /> Alla kunder
        </button>
        <div className="bg-[#121212] border border-[#27272A] rounded-xl p-5 mb-5">
          <h2 className="font-playfair text-xl font-bold text-white">{customer.full_name || "Kund"}</h2>
          <p className="text-[#C0C0C6] text-xs mt-1">
            {[customer.email, customer.phone, customer.source && `via ${customer.source}`].filter(Boolean).join(" · ")}
          </p>
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="bg-[#0A0A0A] rounded-lg p-3 text-center">
              <p className="font-playfair text-xl font-bold text-white">{stats.order_count}</p>
              <p className="text-[10px] uppercase tracking-wider text-[#9A9AA2]">Beställningar</p>
            </div>
            <div className="bg-[#0A0A0A] rounded-lg p-3 text-center">
              <p className="font-playfair text-xl font-bold text-[#D4B483]">{stats.total_spent} kr</p>
              <p className="text-[10px] uppercase tracking-wider text-[#9A9AA2]">Totalt handlat</p>
            </div>
            <div className="bg-[#0A0A0A] rounded-lg p-3 text-center">
              <p className="font-playfair text-sm font-bold text-white mt-1">{stats.last_order_at ? fmt(stats.last_order_at) : "—"}</p>
              <p className="text-[10px] uppercase tracking-wider text-[#9A9AA2] mt-1">Senaste order</p>
            </div>
          </div>
        </div>

        <h3 className="text-sm font-semibold text-white mb-3">Tidslinje <span className="text-[#9A9AA2] font-normal">— allt kunden gjort, från alla delar av systemet</span></h3>
        {events.length === 0 ? (
          <p className="text-[#9A9AA2] text-sm">Inga händelser ännu.</p>
        ) : (
          <div className="space-y-0 relative">
            {events.map((e, i) => {
              const meta = EVENT_META[e.type] || { icon: Sparkles, color: "#9A9AA2", label: () => e.type };
              const Icon = meta.icon;
              let text;
              try { text = meta.label(e.payload || {}); } catch { text = e.type; }
              return (
                <div key={e.id || i} className="flex gap-3 pb-4 relative">
                  {i < events.length - 1 && <div className="absolute left-[13px] top-7 bottom-0 w-px bg-[#27272A]" />}
                  <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 border"
                    style={{ backgroundColor: meta.color + "18", borderColor: meta.color + "40" }}>
                    <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <p className="text-sm text-[#E8E8EA] leading-snug">{text}</p>
                    <p className="text-[11px] text-[#9A9AA2] mt-0.5">{fmt(e.created_at)} · {e.location}{e.source && e.source !== "website" ? ` · ${e.source}` : ""}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-3xl" data-testid="customers-panel">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h2 className="font-playfair text-xl font-bold text-white">Kunder</h2>
          <p className="text-[#C8C8CE] text-xs mt-1">{total} kunder (delade mellan alla restauranger). Klicka för hela historiken.</p>
        </div>
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Sök namn, e-post eller telefon…"
          className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] w-72 h-9" />
      </div>

      {loading || loadingDetail ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#D4B483] animate-spin" /></div>
      ) : customers.length === 0 ? (
        <div className="bg-[#121212] border border-[#27272A] rounded-xl p-10 text-center">
          <Users className="w-8 h-8 text-[#3F3F46] mx-auto mb-3" />
          <p className="text-[#C0C0C6] text-sm">Inga kunder matchar sökningen.</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {customers.map(c => (
            <button key={c.id} onClick={() => openCustomer(c)}
              className="w-full text-left bg-[#121212] rounded-xl border border-[#27272A] hover:border-[#D4B483]/40 p-4 flex items-center gap-3 transition-all">
              <div className="w-9 h-9 rounded-full bg-[#D4B483]/10 flex items-center justify-center shrink-0">
                <User className="w-4 h-4 text-[#D4B483]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium truncate">{c.full_name || c.email}</p>
                <p className="text-[11px] text-[#9A9AA2] truncate">{[c.email, c.phone].filter(Boolean).join(" · ")}</p>
              </div>
              <span className="text-[11px] text-[#9A9AA2] shrink-0">{(c.created_at || "").slice(0, 10)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
